import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "kanpian:favorites";
const SYNC_EVENT = "kanpian:favorites-changed";

function readFavorites(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is number => typeof item === "number");
  } catch {
    return [];
  }
}

/**
 * Favorites stored in localStorage (demo only). Kept in sync across all
 * components on the page via a custom window event.
 */
export function useFavorites() {
  const [ids, setIds] = useState<number[]>(() => readFavorites());

  useEffect(() => {
    const handler = () => setIds(readFavorites());
    window.addEventListener(SYNC_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(SYNC_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const toggle = useCallback((id: number): boolean => {
    const current = readFavorites();
    const exists = current.includes(id);
    const next = exists ? current.filter((x) => x !== id) : [...current, id];
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore write errors */
    }
    setIds(next);
    window.dispatchEvent(new Event(SYNC_EVENT));
    return next.includes(id);
  }, []);

  const isFavorite = useCallback((id: number) => ids.includes(id), [ids]);

  return { ids, toggle, isFavorite };
}